import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router";

import { t } from "i18next";

import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  SxProps,
  Theme,
  Typography,
  useMediaQuery
} from "@mui/material";

import { theme } from "@/src/theme/theme";

import { articleFilenameToSlugAtom } from "@/src/state/articleFilenameToSlugAtom";
import { articleListAtom } from "@/src/state/articleListAtom";
import { articlesAtom } from "@/src/state/articlesAtom";
import { userAuctionsBadgesAtom } from "@/src/state/userAuctionsBadgesAtom";

import { BreadcrumbPanel, PageContainer } from "@/src/components/atoms";
import { FAQContainer } from "@/src/components/molecules/FAQContainer/FAQContainer";

import {
  ArticleBodyItem,
  type ArticleBodyItemRenderCtaParams
} from "@/src/pages/consumer/BlogPage/components/ArticleBodyItem";
import { ArticleCard } from "@/src/pages/consumer/BlogPage/components/ArticleCard";
import { BlogPostSkeleton } from "@/src/pages/consumer/BlogPostPage/components/BlogPostSkeleton";

import { LoginSource } from "@/src/auth/auth";
import { useAuth } from "@/src/auth/hooks/useAuth";
import { vehicleTypeFromAuctionHref } from "@/src/constants/routes";
import { VehicleType } from "@/src/types/types";
import { articlesFromFilenameList } from "@/src/utils/articleSyncHelpers";
import {
  Article,
  faqItemsExist,
  formatArticleDate,
  getArticleUrl,
  normalizeArticleBody,
  useGetArticles
} from "@/src/utils/blogUtils";

import { dashesToUnderscores } from "../BlogPage/BlogPage";
import {
  CONSUMER_HOMEPAGE_PREFIX,
  HomePageButton
} from "../HomePage/HomePageContent";
import { onAuctionListingClick } from "../HomePage/homePageUtils";

import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { useAtom, useAtomValue } from "jotai";

const articleAuctionCtaRowSx: SxProps<Theme> = {
  mb: 3,
  mt: 1,
  width: "fit-content",
  maxWidth: "100%"
};

const articleAuctionCtaHomePageButtonSx: SxProps<Theme> = {
  backgroundColor: theme.palette.semantic.primary.main,
  width: "fit-content",
  maxWidth: "100%",
  minWidth: "unset",
  boxShadow: 2,
  px: 2.5,
  py: 1.25,
  "&.MuiButton-outlined.MuiButton-colorPrimary:hover": {
    backgroundColor: theme.palette.misc.coldBackground,
    boxShadow: theme.shadows[2]
  }
};

const articleAuctionCtaButtonTextSx: SxProps<Theme> = {
  fontSize: theme.typography.buttonSmall.fontSize,
  fontWeight: theme.typography.fontWeightBold,
  textTransform: "uppercase",

  lineHeight: 1.25
};

const BlogPostPage = () => {
  const { isAuthenticated, user, login } = useAuth();
  const [userAuctionsBadges] = useAtom(userAuctionsBadgesAtom);
  const draftAuction = userAuctionsBadges?.draftAuctionId;
  const activeAuction = userAuctionsBadges?.activeAuctionId;

  let buttonText = `${CONSUMER_HOMEPAGE_PREFIX}.startAuction`;

  if (draftAuction) {
    buttonText = `${CONSUMER_HOMEPAGE_PREFIX}.continueAuction`;
  }
  const filenames = useAtomValue(articleListAtom);
  const filenameToSlug = useAtomValue(articleFilenameToSlugAtom);
  const articles = useAtomValue(articlesAtom);
  const sendDataToGTM = useGTMDispatch();
  const navigate = useNavigate();
  const { isLoading } = useGetArticles();
  const { title } = useParams<{ title: string }>();
  const article: Article | undefined = articles[title ?? ""];
  const isMobile = useMediaQuery("(max-width: 631px)");
  const isDesktop = useMediaQuery(theme.breakpoints.down("lg"));

  const currentSlug = title ?? "";
  let fourArticles: Article[] = [];
  if (article != null) {
    fourArticles = articlesFromFilenameList(filenames, filenameToSlug, articles)
      .filter((a) => getArticleUrl(a.title) !== currentSlug)
      .slice(0, 4);
  }

  const handleSendGtmEvent = (gtmEvent: string) => {
    sendDataToGTM({
      event: { gtmEvent },
      user_type: "consumer"
    });
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, [title]);

  if (isLoading) {
    return <BlogPostSkeleton />;
  }

  if (!article) {
    return (
      <PageContainer maxWidth="1200px" sx={{ margin: "auto" }}></PageContainer>
    );
  }

  const articleDateGtmSuffix = dashesToUnderscores(article.date);
  const displayBody = normalizeArticleBody(article);

  const articleCtaGtmEvent = (
    params: ArticleBodyItemRenderCtaParams
  ): string => {
    if (params.legacyGtmSlot === "afterIngress") {
      return `article_cta_after_ingress_${articleDateGtmSuffix}_clicked`;
    }
    if (params.legacyGtmSlot === "afterBody") {
      return `article_cta_after_body_${articleDateGtmSuffix}_clicked`;
    }
    return `article_cta_body_${params.bodyIndex}_${articleDateGtmSuffix}_clicked`;
  };

  const handleArticleAuctionCtaClick = (
    gtmEventName: string,
    ctaHref?: string
  ) => {
    handleSendGtmEvent(gtmEventName);
    onAuctionListingClick(
      LoginSource.ARTICLE_PAGE_CTA,
      isAuthenticated,
      draftAuction,
      activeAuction,
      navigate,
      user,
      sendDataToGTM,
      login,
      vehicleTypeFromAuctionHref(ctaHref)
    );
  };

  const moreArticlesHeadingId = "related-articles-heading";
  const ctaSectionHeadingId = "article-cta-heading";

  return (
    <PageContainer>
      <BreadcrumbPanel
        crumbs={[
          {
            id: "homePageCrumb",
            label: t("consumer.pageTitle.home"),
            url: "/"
          },
          {
            id: "blogPageCrumb",
            label: t("consumer.pageTitle.blog"),
            url: "/blog"
          }
        ]}
        sx={{ pl: [2, 3], pr: [2, 3] }}
      />
      <PageContainer maxWidth="800px" sx={{ margin: "auto" }}>
        <article>
          <Typography
            component="h1"
            variant="h1"
            sx={{ mb: 3, display: "block" }}
          >
            {article.title}
          </Typography>

          <Box
            sx={{
              width: "100%",
              aspectRatio: "16 / 9",
              minHeight: "200px",
              mb: 3,
              position: "relative"
            }}
          >
            <img
              src={article.mainImage.url}
              alt={article.mainImage.caption}
              width={800}
              height={450}
              fetchPriority="high"
              decoding="async"
              style={{
                width: "100%",
                height: "100%",
                margin: "0 auto",
                aspectRatio: "16 / 9",
                objectFit: "cover"
              }}
            />
          </Box>

          <Typography
            component="time"
            variant="body1"
            dateTime={article.date}
            sx={{
              display: "block",
              mb: 1,
              color: theme.palette.text.secondary,
              fontWeight: theme.typography.fontWeightMedium
            }}
          >
            {formatArticleDate(article.date)}
          </Typography>

          <Typography variant="subtitle1" sx={{ mb: 3, display: "block" }}>
            {article.ingress}
          </Typography>

          <Box sx={{ lineHeight: 1.6 }}>
            {displayBody.length > 0
              ? displayBody.map((content, idx) => (
                  <ArticleBodyItem
                    data={content}
                    isLast={idx === displayBody.length - 1}
                    bodyIndex={idx}
                    key={`${article.date}-body-item-${idx}`}
                    renderArticleCtaButton={(params) => (
                      <Box sx={articleAuctionCtaRowSx}>
                        <HomePageButton
                          variant="contained"
                          size="small"
                          textVariant="inherit"
                          textComponent="span"
                          textSx={articleAuctionCtaButtonTextSx}
                          sx={articleAuctionCtaHomePageButtonSx}
                          onClick={() =>
                            handleArticleAuctionCtaClick(
                              articleCtaGtmEvent(params),
                              params.href
                            )
                          }
                          buttonText={params.text}
                          color="primary"
                        />
                      </Box>
                    )}
                  />
                ))
              : null}
          </Box>

          {article.faqItemList && faqItemsExist(article.faqItemList) ? (
            <Container disableGutters sx={{ my: 4 }}>
              <FAQContainer showIds={article.faqItemList} />
            </Container>
          ) : null}

          <Box
            component="section"
            aria-labelledby={ctaSectionHeadingId}
            sx={{
              backgroundColor: theme.palette.semantic.primary.hover,
              p: 2,
              mt: 4
            }}
          >
            <Typography id={ctaSectionHeadingId} sx={{ pb: 0 }} variant="h2">
              {t("components.articlePage.title")}
            </Typography>
            <Box
              component="ol"
              sx={{
                pl: 2.5,
                m: 0,
                "& li": { mb: 1.5 }
              }}
            >
              <li>{t("components.articlePage.step1")}</li>
              <li>{t("components.articlePage.step2")}</li>
              <li>{t("components.articlePage.step3")}</li>
              <li>{t("components.articlePage.step4")}</li>
            </Box>
            <Button
              sx={{ mt: 2 }}
              variant="contained"
              aria-label={t(buttonText)}
              onClick={() =>
                onAuctionListingClick(
                  LoginSource.ARTICLE_PAGE_CTA,
                  isAuthenticated,
                  draftAuction,
                  activeAuction,
                  navigate,
                  user,
                  sendDataToGTM,
                  login,
                  VehicleType.CAR
                )
              }
            >
              {t(buttonText)}
            </Button>
          </Box>
        </article>
      </PageContainer>
      <Divider />
      <Box
        component="section"
        aria-labelledby={moreArticlesHeadingId}
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 4,
          pb: 4
        }}
      >
        <Box>
          <Typography
            id={moreArticlesHeadingId}
            component="h2"
            variant="h4"
            sx={{
              mb: 3,
              textAlign: "center",
              textTransform: "uppercase"
            }}
          >
            {t("components.articlePage.moreArticles")}
          </Typography>
          <Grid container sx={{ maxWidth: isDesktop ? 1000 : "100%" }}>
            {fourArticles.map((relatedArticle) => (
              <Grid
                key={`blog-article-card-grid-${getArticleUrl(
                  relatedArticle.title
                )}`}
                size={{
                  xs: 12,
                  sm: 6,
                  lg: 3
                }}
              >
                <ArticleCard
                  to={`/blog/${getArticleUrl(relatedArticle.title)}`}
                  articleData={relatedArticle}
                  titleSize="medium"
                  onClick={() => {
                    handleSendGtmEvent(
                      `article_card_${dashesToUnderscores(
                        relatedArticle.date
                      )}_clicked`
                    );
                  }}
                />
              </Grid>
            ))}
          </Grid>
          <Box
            sx={{
              display: "flex",
              justifyContent: isMobile ? "center" : "flex-end"
            }}
          >
            <Button
              onClick={() => {
                handleSendGtmEvent("blog_post_page_show_all_clicked");
                navigate("/blog");
              }}
              variant="text"
            >
              {t("general.showAll")}
            </Button>
          </Box>
        </Box>
      </Box>
    </PageContainer>
  );
};

export default BlogPostPage;
